import { useEffect, useState } from "react";
import type { MedicalEvent, PtProgramPayload } from "@medassist/shared";
import { listPtPrograms, ApiError } from "../../../lib/api.js";
import { ActivityIcon } from "../../icons.js";
import type { WidgetProps } from "../types.js";

function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

function programLabel(payload: PtProgramPayload): string {
  return payload.sheetTitle ?? payload.suspectedDiagnosis ?? payload.bodyRegion ?? "PT Program";
}

function countExercises(payload: PtProgramPayload): number {
  if (payload.source === "scanned") {
    return payload.customExercises?.length ?? 0;
  }
  return payload.exerciseIds?.length ?? 0;
}

export function PtReminderWidget({ patient, events, onNavigate, onError }: WidgetProps) {
  const [programs, setPrograms] = useState<MedicalEvent[] | null>(null);

  useEffect(() => {
    listPtPrograms(patient.patientId)
      .then(setPrograms)
      .catch((err) => onError(err instanceof ApiError ? err.message : "Couldn't load PT programs"));
  }, [patient.patientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const today = todayKey();
  const sessionsToday = events.filter(
    (e) => e.eventType === "pt_session" && e.occurredAt.slice(0, 10) === today
  );
  // A confirmed "didn't do it" session (CATCHUP_WIDGETS_PLAN.md D-CU-10) still counts
  // as handled today (no amber nag), but is shown as a neutral skip, not a done ✓.
  const programIdsWithSessionToday = new Set(
    sessionsToday.map((e) => (e.payload as Record<string, unknown>).programEventId as string)
  );
  const skippedProgramIds = new Set(
    sessionsToday
      .filter((e) => (e.payload as Record<string, unknown>).skipped)
      .map((e) => (e.payload as Record<string, unknown>).programEventId as string)
  );

  const activePrograms = programs ?? [];
  const pendingPrograms = activePrograms.filter((p) => !programIdsWithSessionToday.has(p.eventId));

  const totalExercises = pendingPrograms.reduce((sum, p) => {
    return sum + countExercises(p.payload as unknown as PtProgramPayload);
  }, 0);

  if (programs === null) {
    return (
      <section className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
        <div className="mb-2 flex items-center gap-2">
          <ActivityIcon size={16} className="text-blue-500 dark:text-blue-400" />
          <h2 className="font-semibold text-gray-900 dark:text-gray-100">PT reminder</h2>
        </div>
        <p className="text-sm text-gray-400 dark:text-gray-500">Loading…</p>
      </section>
    );
  }

  if (activePrograms.length === 0) {
    return null;
  }

  const allDone = pendingPrograms.length === 0;
  const allSkipped = allDone && activePrograms.every((p) => skippedProgramIds.has(p.eventId));

  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
      <div className="mb-2 flex items-center gap-2">
        <ActivityIcon
          size={16}
          className={
            allDone
              ? allSkipped
                ? "text-gray-400 dark:text-gray-500"
                : "text-green-600 dark:text-green-400"
              : "text-amber-500 dark:text-amber-400"
          }
        />
        <h2 className="font-semibold text-gray-900 dark:text-gray-100">PT reminder</h2>
        <button onClick={() => onNavigate("physical-therapy")} className="ml-auto text-xs text-(--accent) hover:underline">
          Go to PT
        </button>
      </div>
      {allDone ? (
        allSkipped ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">PT skipped today</p>
        ) : (
          <p className="text-sm text-green-700 dark:text-green-300">
            PT done for today ✓
          </p>
        )
      ) : (
        <div className="space-y-2">
          <p className="text-sm text-amber-700 dark:text-amber-300">
            {"You haven't done your PT today"}
            {totalExercises > 0 ? ` — ${totalExercises} exercise${totalExercises !== 1 ? "s" : ""}` : ""}
          </p>
          <div className="space-y-1">
            {pendingPrograms.slice(0, 3).map((p) => {
              const payload = p.payload as unknown as PtProgramPayload;
              return (
                <div key={p.eventId} className="flex items-center justify-between text-sm">
                  <span className="truncate text-gray-700 dark:text-gray-300">{programLabel(payload)}</span>
                  <button
                    onClick={() => onNavigate("physical-therapy")}
                    className="ml-2 shrink-0 rounded-full bg-amber-100 dark:bg-amber-900/40 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-800/40"
                  >
                    Do it
                  </button>
                </div>
              );
            })}
            {pendingPrograms.length > 3 && (
              <p className="text-xs text-gray-400 dark:text-gray-500">+{pendingPrograms.length - 3} more</p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
